import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getApiUrl } from '../utils/apiConfig';
import { getUserData } from '../utils/userUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faChartBar,
  faThumbtack,
  faTrash,
  faPlus,
  faEllipsisV,
  faUserCog,
  faUserSecret,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import './Sidebar.css';
import { isAdmin, isSuperAdmin, canSendMessage, canManageChatHistory } from '../utils/permissions';
import { brandLogos } from '../utils/branding';
import { useAccount } from '../contexts/AccountContext';

function Sidebar({ onLogout, user, onChatSelect, onNewChat, chatHistoryVersion, selectedChat, onEndImpersonation }) {
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState([]);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(256); // Default width (64 * 4 = 256px)
  const [loadingOperations, setLoadingOperations] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');

  // Use the account context
  const {
    accounts,
    selectedAccountId,
    selectAccount,
    getAccountIdToUse
  } = useAccount();

  const sidebarRef = useRef(null);
  const dropdownRef = useRef(null);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isLoadingRef = useRef(false);
  const isInitialRenderRef = useRef(true);

  // Create a ref to store the debounce timer
  const debounceTimerRef = useRef(null);

  // Create a ref to store the last request timestamp
  const lastRequestTimeRef = useRef(0);

  // Create a ref to store the last request parameters
  const lastRequestParamsRef = useRef(null);

  // Create a ref to store pending request parameters
  const pendingRequestRef = useRef(null);

  // Actual implementation of chat history loading
  const loadChatHistoryImpl = useCallback(async (overrideAccountId = null) => {
    // Skip if user is not loaded yet
    if (!user) {
      console.log('Skipping chat history load due to user not loaded');
      return;
    }

    // For regular admins with any accounts, don't load chat history until an account is selected
    if (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0) {
      if (!overrideAccountId && !selectedAccountId) {
        console.log('Regular admin with accounts but no account selected, clearing chat history');
        setChatHistory([]);
        return;
      }
    }

    try {
      isLoadingRef.current = true;
      const { userId } = getUserData();
      if (!userId) {
        console.error('No valid user session found');
        isLoadingRef.current = false;
        return;
      }

      // Use the configured API instance that includes the API key in headers
      const apiUrl = getApiUrl('/api/chat-history');

      // Create params object with userId and accountId
      const params = { userId };

      // Use the getAccountIdToUse function from AccountContext
      // This ensures we use the same account ID across all components
      // If an override is provided, use that instead
      let accountIdToUse = overrideAccountId || getAccountIdToUse();

      if (accountIdToUse) {
        params.accountId = accountIdToUse;
      }

      // Store the current request params for future deduplication
      const requestParams = JSON.stringify(params);
      lastRequestParamsRef.current = requestParams;

      console.log('Loading chat history with params:', params);
      const response = await api.get(apiUrl, { params });
      setChatHistory(response.data);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      isLoadingRef.current = false;
      pendingRequestRef.current = null;
    }
  }, [getAccountIdToUse, user, isAdmin, isSuperAdmin, accounts.length, selectedAccountId]);

  // Debounced wrapper for loadChatHistory
  const loadChatHistory = useCallback((overrideAccountId = null) => {
    // Check for the global block flag
    if (window.blockInitialChatHistoryCall === true) {
      console.log('Skipping chat history load due to block flag');
      return;
    }

    // Prevent duplicate requests
    if (isLoadingRef.current) {
      console.log('Skipping chat history load due to already loading');
      return;
    }

    // Create params object to check for duplicates
    const { userId } = getUserData();
    if (!userId) {
      console.error('No valid user session found');
      return;
    }

    const params = { userId };
    let accountIdToUse = overrideAccountId || getAccountIdToUse();
    if (accountIdToUse) {
      params.accountId = accountIdToUse;
    }

    // Store the pending request parameters
    pendingRequestRef.current = JSON.stringify(params);

    // We'll update the timestamp when the actual request is made

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new timer
    debounceTimerRef.current = setTimeout(() => {
      // Only proceed if this is still the most recent request
      if (pendingRequestRef.current === JSON.stringify(params)) {
        lastRequestTimeRef.current = Date.now();
        loadChatHistoryImpl(overrideAccountId);
      }
      debounceTimerRef.current = null;
    }, 200);
  }, [loadChatHistoryImpl, getAccountIdToUse]);

  // Function to initialize chat history loading based on user type
  const initializeChatHistory = useCallback(() => {
    // Set a global flag to block the initial chat-history API call
    window.blockInitialChatHistoryCall = true;

    // Only for regular admins (not super admins)
    if (!isAdmin(user) || isSuperAdmin(user)) {
      // For non-admins or super admins, just load chat history directly
      window.blockInitialChatHistoryCall = false;
      loadChatHistory();
      return;
    }

    // For admins with accounts
    if (accounts.length === 0) {
      // No accounts, just load chat history with default accountId
      window.blockInitialChatHistoryCall = false;
      loadChatHistory();
    } else {
      // For any number of accounts, don't auto-select any account
      // Let the user explicitly select an account
      setChatHistory([]);
      // Keep the block flag true until user selects an account
    }
  }, [user, accounts.length, loadChatHistory]);

  // Initialize the global flag on component mount
  useEffect(() => {
    // Initialize the flag to false
    window.blockInitialChatHistoryCall = false;

    // We'll handle account fetching in AccountContext
    // Don't call fetchAccounts here to avoid duplicate calls
  }, []);

  // Handle account selection changes
  useEffect(() => {
    console.log('Account selection effect triggered, selectedAccountId:', selectedAccountId);

    // Only proceed if we have both a user and a selected account
    if (user && isAdmin(user) && !isSuperAdmin(user)) {
      if (selectedAccountId) {
        console.log('Regular admin with selected account, loading chat history');

        // Use a small timeout to ensure all state updates have completed
        // This helps prevent race conditions with other effects
        const timeoutId = setTimeout(() => {
          // Double-check that the flag isn't set
          if (window.blockInitialChatHistoryCall !== true) {
            console.log('Loading chat history with accountId:', selectedAccountId);
            loadChatHistory(selectedAccountId);
          } else {
            console.log('Skipping chat history load due to block flag');
          }
        }, 50);

        // Clean up the timeout if the component unmounts or the effect re-runs
        return () => {
          clearTimeout(timeoutId);
        };
      } else {
        console.log('Regular admin with no selected account, clearing chat history');
        setChatHistory([]);
      }
    }
  }, [user, selectedAccountId, loadChatHistory, isAdmin, isSuperAdmin]);

  // Initial load when component first mounts
  useEffect(() => {
    // Skip if user is not loaded yet
    if (!user) return;

    // This is our main entry point - initialize chat history based on user type
    initializeChatHistory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accounts]); // Run when user or accounts change

  // Handle chat history version changes after initial load
  useEffect(() => {
    // Skip the first render
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }

    console.log('Chat history version changed, refreshing chat history');

    // For subsequent renders, just load chat history normally
    // For regular admins with multiple accounts, this will still respect the selectedAccountId

    // Temporarily unblock chat history loading to ensure refresh works with account selector
    const wasBlocked = window.blockInitialChatHistoryCall;
    window.blockInitialChatHistoryCall = false;

    // Force a refresh of the chat history
    // Our debounced loadChatHistory will handle duplicate prevention
    loadChatHistory();

    // Restore the block flag if it was set
    if (wasBlocked) {
      // Use a small delay to ensure the chat history is loaded before blocking again
      setTimeout(() => {
        window.blockInitialChatHistoryCall = wasBlocked;
      }, 300); // Increased timeout to account for debounce
    }
  }, [chatHistoryVersion, loadChatHistory]);

  // Special handler for impersonation and account loading
  useEffect(() => {
    // Listen for impersonation events
    const handleImpersonation = (event) => {
      // Reset the initial render flag to ensure chat history is loaded
      isInitialRenderRef.current = true;
      // Reset the loading flag to allow new API calls
      isLoadingRef.current = false;
      // Clear the block flag to allow chat history loading
      window.blockInitialChatHistoryCall = false;

      // Get the impersonated user from the event if available
      const impersonatedUser = event.detail?.impersonatedUser;
      console.log('Impersonation event with user:', impersonatedUser);

      // Use a small delay to ensure the user data is updated before loading chat history
      setTimeout(() => {
        // Force a refresh of the chat history
        // If we have an impersonated user with an accountId, use it as an override
        if (impersonatedUser && (impersonatedUser.accountId || impersonatedUser.account_id)) {
          const accountIdToUse = impersonatedUser.accountId || impersonatedUser.account_id;
          console.log(`[${event.timeStamp}] Loading chat history with impersonated user accountId:`, accountIdToUse);
          // Our debounced loadChatHistory will handle duplicate prevention
          loadChatHistory(accountIdToUse);
        } else {
          console.log(`[${event.timeStamp}] Loading chat history for impersonated user without accountId`);
          // Our debounced loadChatHistory will handle duplicate prevention
          loadChatHistory();
        }
      }, 500);
    };

    // Listen for accounts loaded event
    const handleAccountsLoaded = (event) => {
      const eventId = Date.now();
      const { selectedAccountId: eventAccountId, timestamp } = event.detail;

      console.log(`[${eventId}] Accounts loaded event received, timestamp:`, timestamp);

      // We don't need to trigger chat history loading here
      // The useEffect with selectedAccountId dependency will handle that
      // This prevents duplicate chat history loading

      // Just log the event for debugging
      if (eventAccountId) {
        console.log(`[${eventId}] Event has selectedAccountId:`, eventAccountId);
      } else {
        console.log(`[${eventId}] Event has no selectedAccountId`);
      }
    };

    // Listen for end impersonation events
    const handleEndImpersonation = (event) => {
      // Reset the initial render flag to ensure chat history is loaded
      isInitialRenderRef.current = true;
      // Reset the loading flag to allow new API calls
      isLoadingRef.current = false;
      // Clear the block flag to allow chat history loading
      window.blockInitialChatHistoryCall = false;

      // Get the original user from the event if available
      const originalUser = event.detail?.originalUser;
      console.log('End impersonation event with user:', originalUser);

      // Use a small delay to ensure the user data is updated before loading chat history
      setTimeout(() => {
        // Force a refresh of the chat history
        // If we have an original user with an accountId, use it as an override
        if (originalUser && (originalUser.accountId || originalUser.account_id)) {
          const accountIdToUse = originalUser.accountId || originalUser.account_id;
          console.log(`[${event.timeStamp}] Loading chat history with original user accountId:`, accountIdToUse);
          // Our debounced loadChatHistory will handle duplicate prevention
          loadChatHistory(accountIdToUse);
        } else {
          console.log(`[${event.timeStamp}] Loading chat history for original user without accountId`);
          // Our debounced loadChatHistory will handle duplicate prevention
          loadChatHistory();
        }
      }, 500);
    };

    window.addEventListener('userImpersonation', handleImpersonation);
    window.addEventListener('endUserImpersonation', handleEndImpersonation);
    window.addEventListener('accountsLoaded', handleAccountsLoaded);

    return () => {
      window.removeEventListener('userImpersonation', handleImpersonation);
      window.removeEventListener('endUserImpersonation', handleEndImpersonation);
      window.removeEventListener('accountsLoaded', handleAccountsLoaded);
    };
  }, [loadChatHistory]);

  // Handle account selection change
  const handleAccountChange = (e) => {
    const newAccountId = e.target.value;

    console.log('Account selection changed to:', newAccountId);

    // Set the block flag to prevent premature chat history loading
    window.blockInitialChatHistoryCall = true;

    // Reset loading state to prevent race conditions
    isLoadingRef.current = false;

    // Clear the selected chat to reset the main chat area
    if (onChatSelect) {
      onChatSelect(null);
    }

    // If account selection is cleared, clear chat history immediately
    if (!newAccountId) {
      setChatHistory([]);
    }

    // Use the context to update the selected account
    // This will handle localStorage and events
    selectAccount(newAccountId);

    // The useEffect with selectedAccountId dependency will handle loading chat history
    // We don't need to call loadChatHistory here

    // Unblock chat history loading after a short delay
    setTimeout(() => {
      window.blockInitialChatHistoryCall = false;
    }, 50);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdownId &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.sidebar-action-button') // Don't close if clicking on the action button
      ) {
        setOpenDropdownId(null);
      }
    };

    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openDropdownId]);

  const handlePinChat = async (chatId, e) => {
    e.stopPropagation();
    try {
      // Set loading state for this operation
      setLoadingOperations(prev => ({ ...prev, [chatId]: 'pin' }));

      // Find the chat to update its pinned status
      const chatToUpdate = chatHistory.find(chat => chat.id === chatId);
      const newPinnedStatus = !chatToUpdate.pinned;

      // Update the API
      const { userId } = getUserData();
      // For regular admins with multiple accounts, require an account selection
      if (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 1 && !selectedAccountId) {
        alert('Please select an account first');
        setLoadingOperations(prev => {
          const newState = { ...prev };
          delete newState[chatId];
          return newState;
        });
        setOpenDropdownId(null);
        return;
      }

      // Use the getAccountIdToUse function from AccountContext
      // This ensures we use the same account ID across all components
      const accountIdToUse = getAccountIdToUse();

      // Explicitly add accountId to the request
      await api.post(getApiUrl(`/api/chat-history/${chatId}/pin`), {
        userId,
        accountId: accountIdToUse
      });

      // Update locally instead of reloading
      setChatHistory(prevChatHistory =>
        prevChatHistory.map(chat =>
          chat.id === chatId
            ? { ...chat, pinned: newPinnedStatus }
            : chat
        )
      );
    } catch (error) {
      console.error('Error pinning chat:', error);
      // If error occurs, reload to ensure consistency
      loadChatHistory();
    } finally {
      // Clear loading state
      setLoadingOperations(prev => {
        const newState = { ...prev };
        delete newState[chatId];
        return newState;
      });
      setOpenDropdownId(null);
    }
  };

  const handleRemoveChat = async (chatId, e) => {
    e.stopPropagation();
    try {
      const chat = chatHistory.find(c => c.id === chatId);
      if (chat?.pinned) {
        alert('Please unpin the chat before removing it.');
        setOpenDropdownId(null);
        return;
      }

      // Set loading state for this operation
      setLoadingOperations(prev => ({ ...prev, [chatId]: 'remove' }));

      // Delete the chat first to prevent race conditions
      const { userId } = getUserData();

      // For regular admins with multiple accounts, require an account selection
      if (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 1 && !selectedAccountId) {
        alert('Please select an account first');
        setLoadingOperations(prev => {
          const newState = { ...prev };
          delete newState[chatId];
          return newState;
        });
        setOpenDropdownId(null);
        return;
      }

      // Use the getAccountIdToUse function from AccountContext
      // This ensures we use the same account ID across all components
      const accountIdToUse = getAccountIdToUse();

      // Explicitly add accountId to the request
      await api.delete(getApiUrl(`/api/chat-history/${chatId}`), {
        params: {
          userId,
          accountId: accountIdToUse
        }
      });

      // Only clear UI state if the deleted chat was selected
      if (selectedChat && selectedChat.id === chatId) {
        // Create a custom event to close the briefcase panel
        const closeBriefcaseEvent = new CustomEvent('closeBriefcase');
        window.dispatchEvent(closeBriefcaseEvent);

        // Clear the selected chat
        onChatSelect(null);
      }

      // Update locally instead of reloading
      setChatHistory(prevChatHistory =>
        prevChatHistory.filter(chat => chat.id !== chatId)
      );
    } catch (error) {
      console.error('Error removing chat:', error);
      alert('Failed to remove chat. Please try again.');
      // If error occurs, reload to ensure consistency
      loadChatHistory();
    } finally {
      // Clear loading state
      setLoadingOperations(prev => {
        const newState = { ...prev };
        delete newState[chatId];
        return newState;
      });
      setOpenDropdownId(null);
    }
  };

  const toggleDropdown = (chatId, e) => {
    e.stopPropagation();
    if (openDropdownId !== chatId) {
      const button = e.currentTarget;
      const buttonRect = button.getBoundingClientRect();

      // Calculate if dropdown should appear above or below based on available space
      const dropdownHeight = 100; // Approximate height of dropdown with 2 items
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spacing = 8; // Spacing for visual appeal

      // Set position based on available space
      let position;
      if (spaceBelow < dropdownHeight + spacing && buttonRect.top > dropdownHeight + spacing) {
        position = 'top';
      } else {
        position = 'bottom';
      }
      setDropdownPosition(position);

      // Set dropdown coordinates for fixed positioning
      setDropdownCoords({
        top: position === 'top' ? buttonRect.top - dropdownHeight - spacing : buttonRect.bottom + spacing,
        left: buttonRect.right - 160 // Width of dropdown
      });

      setOpenDropdownId(chatId);
    } else {
      setOpenDropdownId(null);
    }
  };

  const handleRename = async (e) => {
    if (e.type === 'keydown' && e.key !== 'Enter') return;
    if (!editingTitle.trim()) {
      setEditingChatId(null);
      setEditingTitle('');
      return;
    }

    try {
      // For regular admins with multiple accounts, require an account selection
      if (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 1 && !selectedAccountId) {
        alert('Please select an account first');
        setEditingChatId(null);
        setEditingTitle('');
        return;
      }

      // Use the getAccountIdToUse function from AccountContext
      // This ensures we use the same account ID across all components
      const accountIdToUse = getAccountIdToUse();

      // Explicitly add accountId to the request
      const response = await api.patch(getApiUrl(`/api/chat-history/${editingChatId}`), {
        summary: editingTitle,
        userId: user.id,
        accountId: accountIdToUse
      });

      if (response.data.error) {
        console.error('Error updating chat summary:', response.data.error);
        alert('Failed to update chat name. Please try again.');
      } else {
        // Update the chat summary locally instead of reloading the entire chat history
        setChatHistory(prevChatHistory =>
          prevChatHistory.map(chat =>
            chat.id === editingChatId
              ? { ...chat, summary: editingTitle }
              : chat
          )
        );
      }
    } catch (error) {
      console.error('Error updating chat summary:', error);
      alert('Failed to update chat name. Please try again.');
    }

    setEditingChatId(null);
    setEditingTitle('');
  };

  const handleResizeStart = (e) => {
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;

    // Add the event listeners to the window for better tracking
    window.addEventListener('mousemove', handleResize, { passive: true });
    window.addEventListener('mouseup', handleResizeEnd);

    // Add a resize class to the body to disable text selection and set cursor
    document.body.classList.add('resizing');
    document.body.classList.add('resizing-sidebar');
    document.body.style.cursor = 'ew-resize';
  };

  const handleResize = (e) => {
    if (!isResizingRef.current) return;

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.max(200, Math.min(window.innerWidth * 0.3, startWidthRef.current + delta));

      // Only update if the width has changed by at least 1px
      if (Math.abs(newWidth - sidebarWidth) >= 1) {
        setSidebarWidth(newWidth);
        // Update the CSS variable for sidebar width
        document.documentElement.style.setProperty('--sidebar-width', `${newWidth}px`);
      }
    });
  };

  const handleResizeEnd = () => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    window.removeEventListener('mousemove', handleResize);
    window.removeEventListener('mouseup', handleResizeEnd);

    // Remove the resize class
    document.body.classList.remove('resizing');
    document.body.classList.remove('resizing-sidebar');
    document.body.style.cursor = '';
  };

  // Clean up event listeners and timers on unmount
  useEffect(() => {
    return () => {
      // Clean up resize event listeners
      if (isResizingRef.current) {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', handleResizeEnd);
        document.body.classList.remove('resizing');
        document.body.classList.remove('resizing-sidebar');
        document.body.style.cursor = '';
      }

      // Clean up any pending debounce timer
      if (debounceTimerRef.current) {
        console.log('Component unmounting, clearing debounce timer');
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  // Update sidebar width CSS variable on mount and when it changes
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);

    // Add or remove data-sidebar-default attribute based on whether sidebar is at default width
    if (sidebarWidth === 256) {
      document.documentElement.setAttribute('data-sidebar-default', '');
    } else {
      document.documentElement.removeAttribute('data-sidebar-default');
    }
  }, [sidebarWidth]);

  return (
    <div
      ref={sidebarRef}
      className="h-screen bg-gray-100 border-r border-gray-200 flex flex-col relative"
      style={{
        width: `${sidebarWidth}px`,
        minWidth: `${sidebarWidth}px`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}
    >
      <div className="p-4 flex flex-col h-full">
        <div className="flex flex-col mb-6">
          <div className="mb-4" style={{ height: '48px', position: 'relative' }}>
            <img
              src={brandLogos.main}
              alt="Logo"
              style={{
                height: '48px',
                maxWidth: '160px',
                objectFit: 'contain',
                position: 'absolute',
                left: '0',
                marginLeft: '8px'
              }}
            />
          </div>
          <div className="flex items-center">
            {user.picture_url ? (
              <img src={user.picture_url} alt={user.given_name} className="w-8 h-8 rounded-full mr-2" />
            ) : (
              <span className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-2">
                {(user.given_name || user.display_name || user.email || 'U').charAt(0)}
              </span>
            )}
            <span className="text-lg font-medium">{user.given_name || user.display_name || user.email?.split('@')[0] || 'User'}'s Sidekick</span>
          </div>

          {user.isImpersonating && (
            <div className="mt-2 p-3 bg-yellow-100 border border-yellow-300 rounded-md shadow-sm">
              <div className="flex items-center text-yellow-800 mb-2">
                <FontAwesomeIcon icon={faUserSecret} className="mr-2 text-amber-600" size="lg" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Impersonating</span>
                  <span className="text-base font-bold text-amber-700">{user.display_name || user.email}</span>
                </div>
              </div>
              <button
                onClick={onEndImpersonation}
                className="w-full mt-1 py-2 px-3 bg-amber-200 hover:bg-amber-300 rounded-md text-amber-800 font-medium flex items-center justify-center transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                <span>End Impersonation</span>
              </button>
            </div>
          )}

          {/* Account selector for regular admins. Hidden when the user has
              exactly one account — AccountContext auto-selects it, so the
              selector would just be a one-option dropdown. */}
          {(() => {
            return isAdmin(user) && !isSuperAdmin(user) && accounts.length > 1 && (
              <div className="account-selector">
                <div className="account-info">
                  {selectedAccountId
                    ? "Showing chats for selected account"
                    : "Select an account"}
                </div>
                <div className="relative">
                  <select
                    value={selectedAccountId || ''}
                    onChange={handleAccountChange}
                  >
                    <option value="">-- Please select an account --</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })()}

        </div>

        <div className="mb-4">
          <button
            onClick={onNewChat}
            className={`w-full flex items-center p-2 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors ${
              !canSendMessage(user) || (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId)
                ? 'disabled'
                : ''
            }`}
            disabled={!canSendMessage(user) || (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId)}
            title={
              isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId
                ? "Please select an account first"
                : "Create a new canvas"
            }
          >
            <span className="mr-2 text-base">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <span>New Canvas</span>
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search canvas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 pl-8 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            {searchQuery && (
              <button
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setSearchQuery('')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <nav className="flex-grow overflow-y-auto mb-4 space-y-1 max-h-[calc(100%-170px)]">
          {chatHistory
            .filter(chat => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return chat.summary?.toLowerCase().includes(query);
            })
            .sort((a, b) => {
              if (a.pinned && !b.pinned) return -1;
              if (!a.pinned && b.pinned) return 1;
              return new Date(b.updated_at) - new Date(a.updated_at);
            })
            .map((chat) => {
              const isSelected = selectedChat && selectedChat.id === chat.id;
              return (
                <div key={chat.id} className="relative group">
                  <button
                    id={`chat-${chat.id}`}
                    onClick={() => onChatSelect(chat)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditingChatId(chat.id);
                      setEditingTitle(chat.summary);
                    }}
                    className={`w-full flex items-center p-2 rounded-md transition-colors text-left ${
                      isSelected
                        ? 'bg-primary/10 hover:bg-primary/20 border-l-4 border-primary'
                        : 'hover:bg-gray-300'
                    } relative`}
                  >
                    <span className="mr-2 text-base">
                      {chat.pinned && (
                        <FontAwesomeIcon icon={faThumbtack} className="text-primary" />
                      )}
                    </span>
                    {editingChatId === chat.id ? (
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => handleRename(e)}
                        onBlur={handleRename}
                        className="flex-1 bg-white border border-gray-300 rounded px-1 focus:outline-none focus:border-gray-400"
                        autoFocus
                      />
                    ) : (
                      <span className={`truncate flex-1 ${isSelected ? 'font-medium text-primary' : 'text-gray-700'}`}>
                        {chat.summary}
                      </span>
                    )}
                    <div
                      onClick={(e) => toggleDropdown(chat.id, e)}
                      className="opacity-0 group-hover:opacity-100 hover:bg-gray-300 p-1 rounded transition-opacity mr-1 ml-4 cursor-pointer absolute right-0 top-1/2 transform -translate-y-1/2"
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </div>
                  </button>
                  {openDropdownId === chat.id && (
                    <div
                      ref={dropdownRef}
                      className={`sidebar-dropdown-container ${dropdownPosition === 'top' ? 'position-top' : ''}`}
                      style={{
                        position: 'fixed',
                        top: dropdownCoords.top,
                        left: dropdownCoords.left
                      }}
                    >
                      <button
                        onClick={(e) => handlePinChat(chat.id, e)}
                        className={`sidebar-dropdown-item ${(loadingOperations[chat.id] || !canManageChatHistory(user)) ? 'disabled' : ''}`}
                        disabled={loadingOperations[chat.id] || !canManageChatHistory(user)}
                      >
                        {loadingOperations[chat.id] === 'pin' ? (
                          <>
                            <FontAwesomeIcon icon={faThumbtack} className="mr-2" />
                            <span>{chat.pinned ? 'Unpinning...' : 'Pinning...'}</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faThumbtack} className="mr-2" />
                            <span>{chat.pinned ? 'Unpin' : 'Pin'}</span>
                          </>
                        )}
                      </button>
                      {!chat.pinned && (
                        <button
                          onClick={(e) => handleRemoveChat(chat.id, e)}
                          className={`sidebar-dropdown-item text-red ${(loadingOperations[chat.id] || !canManageChatHistory(user)) ? 'disabled' : ''}`}
                          disabled={loadingOperations[chat.id] || !canManageChatHistory(user)}
                        >
                          {loadingOperations[chat.id] === 'remove' ? (
                            <>
                              <FontAwesomeIcon icon={faTrash} className="mr-2" />
                              <span>Removing...</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTrash} className="mr-2" />
                              <span>Remove</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </nav>

        <div className="mt-auto border-t border-gray-200 pt-4 status-logout-container">
          {/* Disable Status link if user is a regular admin with multiple accounts without selection */}
          <button
            className={`w-full py-2 px-4 mb-2 flex items-center rounded transition-colors ${
              isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-300'
            }`}
            onClick={() => {
              // Only navigate if the button is not disabled
              if (!(isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId)) {
                onChatSelect(null); // Clear selected chat
                navigate('/status');
              }
            }}
            disabled={isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId}
            title={
              isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId
                ? 'Please select an account to view statistics'
                : 'View statistics'
            }
          >
            <FontAwesomeIcon icon={faChartBar} className="mr-2" />
            <span>Status</span>
          </button>
          {isAdmin(user) && (
            <button
              className="w-full py-2 pl-3.5 mb-2 flex items-center text-gray-700 hover:bg-gray-300 rounded transition-colors"
              onClick={() => {
                onChatSelect(null); // Clear selected chat
                navigate('/admin');
              }}
            >
              <FontAwesomeIcon icon={faUserCog} className="mr-1.5" />
              <span>Admin</span>
            </button>
          )}
          <button
            onClick={onLogout}
            className="w-full flex items-center py-2 px-4 rounded-md hover:bg-gray-300 transition-colors text-left text-gray-700"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
            <span>Logout</span>
          </button>
        </div>
      </div>
      {/* Resize handle */}
      <div
        className="absolute top-0 right-0 h-full cursor-ew-resize sidebar-resize-handle"
        style={{
          width: '5px',
          zIndex: 20,
          transition: 'background-color 0.2s ease'
        }}
        onMouseDown={handleResizeStart}
      />
    </div>
  );
}

export default Sidebar;